/**
 * Dark mode (F-122): a personal VIEW preference, never board content — no
 * CRDT, no Yjs doc, just a `data-theme` stamp + one localStorage key, exactly
 * like the camera/zoom state in canvas.ts is per-user and never synced.
 *
 * Precedence, cheapest first:
 *  1. a host-imposed theme (`forceTheme`) always wins and hides the toggle —
 *     no shell uses it today (see 3), it stays for a host that must;
 *  2. an explicit stored choice ("light" | "dark") — the sun/moon toggle;
 *  3. the host's DEFAULT (`setDefaultTheme`): the embedded shell inside the
 *     desktop app starts on the app's own dark look, but the toggle stays
 *     and a reader who switches to light keeps light (2026-10 — the desktop
 *     used to FORCE dark, which took the light theme away with the web
 *     shell's sunset; the preference is per reader, not per shell);
 *  4. otherwise the OS/browser's `prefers-color-scheme` decides, and tracks
 *     it live (no stored choice ⇒ this module keeps listening);
 *  5. tokens.css's `.kola-root` block is the light fallback if none of this
 *     ever runs (module not loaded, e.g. a non-JS render) — never unstyled.
 *
 * WHERE the stamp goes (2026-09): the tokens live on `.kola-root` (ui/
 * KolaRoot.tsx registers every such element here, including the portal layer
 * menus render into), not on <html> — Kola shares a document with the
 * desktop app when embedded and must not restyle it. The web shell's
 * `initTheme()` still stamps <html> too, for anything outside the root
 * (the document's own color-scheme).
 *
 * `initTheme()` must run BEFORE the first paint (called at module scope in
 * main.tsx, before `createRoot(...).render`) so there is no flash of the
 * wrong theme while React boots.
 */

export type Theme = "light" | "dark";

const STORAGE_KEY = "kola.theme";

function darkPreferred(): boolean {
  return typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
    : false;
}

/** The user's EXPLICIT choice, or null when following the system preference. */
export function getStoredTheme(): Theme | null {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw === "light" || raw === "dark" ? raw : null;
}

/** A host-imposed theme; null = the reader decides (every shell today). */
let forced: Theme | null = null;

/**
 * The host's own theme, shown until the reader picks one; null = follow the
 * system preference (the web shell has no host).
 */
let hostDefault: Theme | null = null;

/** Whether the web entry asked for <html> to carry the stamp as well. */
let stampDocument = false;

/**
 * The theme actually in effect right now: forced, else the stored choice,
 * else the host's default, else the system preference.
 */
export function effectiveTheme(): Theme {
  return forced ?? getStoredTheme() ?? hostDefault ?? (darkPreferred() ? "dark" : "light");
}

export function isThemeForced(): boolean {
  return forced !== null;
}

/**
 * Impose a theme (the embedded shell passes its host's) or lift it with
 * null. Applies immediately; the toggle renders nothing while forced.
 */
export function forceTheme(theme: Theme | null): void {
  forced = theme;
  applyTheme(effectiveTheme());
}

/**
 * Set the host's default theme (the embedded shell passes its host's) or
 * clear it with null. Only matters while the reader has made no explicit
 * choice; the toggle stays, and a choice made here is remembered under the
 * same `kola.theme` key the web shell reads.
 */
export function setDefaultTheme(theme: Theme | null): void {
  hostDefault = theme;
  applyTheme(effectiveTheme());
}

type ThemeListener = (theme: Theme) => void;

const listeners = new Set<ThemeListener>();

/**
 * Watch the theme actually in effect. Fires for BOTH ways it can change — an
 * explicit toggle and a live system-preference flip — because both land in
 * `applyTheme`. Returns the unsubscribe.
 *
 * Anything that merely reads a `--color-*` variable from CSS needs none of
 * this (tokens.css re-resolves for free); this is for the code that has to
 * compute FROM those values in JS — the grid's F-17 readable-ink math, which
 * composites a cell fill against `--color-surface`.
 */
export function subscribeTheme(listener: ThemeListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Every element carrying the stamp: each `.kola-root` (ui/KolaRoot.tsx). */
const roots = new Set<HTMLElement>();

/**
 * Make an element a theme root: it gets the current stamp now and every
 * later change. Returns the unregister. Insertion order is preserved, so the
 * first registered root is the one `primaryThemeRoot()` reads tokens from.
 */
export function registerThemeRoot(element: HTMLElement): () => void {
  roots.add(element);
  element.dataset["theme"] = effectiveTheme();
  return () => {
    roots.delete(element);
  };
}

/** The element to read `--color-*` tokens from: the first root, else <html>. */
export function primaryThemeRoot(): HTMLElement {
  for (const element of roots) return element;
  return document.documentElement;
}

/** Stamp `data-theme` on every root so tokens.css's selectors apply. */
function applyTheme(theme: Theme): void {
  if (stampDocument) document.documentElement.dataset["theme"] = theme;
  for (const element of roots) element.dataset["theme"] = theme;
  for (const listener of listeners) listener(theme);
}

/**
 * Apply the current effective theme immediately, and keep it live: while
 * there is no explicit stored choice, a system-theme change (the OS switches
 * at sunset, say) re-applies without a reload. Call once, at module scope,
 * before the first render — the WEB shell's entry only; the embedded shell
 * calls `forceTheme` instead and leaves the host's document alone.
 */
export function initTheme(): void {
  stampDocument = true;
  applyTheme(effectiveTheme());
  if (typeof window.matchMedia !== "function") return;
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (getStoredTheme() === null && hostDefault === null) applyTheme(effectiveTheme());
  });
}

/** Set (or clear, with `null`) the user's explicit theme choice and apply it. */
export function setTheme(theme: Theme | null): void {
  if (theme === null) window.localStorage.removeItem(STORAGE_KEY);
  else window.localStorage.setItem(STORAGE_KEY, theme);
  applyTheme(effectiveTheme());
}

/** Flip between light and dark, always leaving an EXPLICIT choice behind. */
export function toggleTheme(): Theme {
  const next: Theme = effectiveTheme() === "dark" ? "light" : "dark";
  setTheme(next);
  return next;
}
